import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { motion } from 'framer-motion';
import {
  Container, Typography, Box, Button, CircularProgress, Alert, TextField,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useWorkflowState } from '@/context/WorkflowContext';
import { useFirebase } from '@/context/FirebaseContext';
import { Check } from 'lucide-react';

// Stripe Publishable Key
// Note: Ensure the app is deployed on HTTPS in production for live Stripe.js integration
const stripePromise = loadStripe('pk_test_51RBdW178DLkwVCwCVUO2azwaEIGyNbB7vnGqkVYjA6cqdMyJgqCjnVSbs9jzdo2NF98LF7Q91rMEgJYPikIkcL7000OMUouJD5');

// Note: Monitor Stripe's documentation for third-party cookie phase-out updates (Chrome warning)

// Styled Components
const CustomButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #9333ea 0, #c084fc)',
  color: '#fff',
  padding: theme.spacing(1, 3),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(147, 51, 234, 0.3)',
  animation: 'pulse 1.5s infinite ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    background: 'linear-gradient(135deg, #7e22ce, #a855f7)',
    boxShadow: '0 0 20px rgba(147, 51, 234, 0.5)',
    animation: 'none',
  },
  '&:disabled': {
    background: 'rgba(147, 51, 234, 0.2)',
    color: 'rgba(255, 255, 255, 0.3)',
    animation: 'none',
  },
}));

const PaymentFormContainer = styled(motion(Box))(({ theme }) => ({
  padding: '16px',
  paddingTop: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #1f2937, #9333ea)',
  border: '1px solid rgba(147, 51, 234, 0.3)',
  width: '100%',
  maxWidth: '90vw',
  '@media (min-width: 640px)': {
    maxWidth: '500px',
  },
  margin: '0 auto',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  minHeight: '200px',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: '12px',
    left: '12px',
    width: '40px',
    height: '30px',
    background: 'linear-gradient(45deg, #d4af37, #f0e68c)',
    borderRadius: '4px',
    boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.5)',
    zIndex: 30,
  },
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    background: 'linear-gradient(145deg, #2d3748, #1f2937)',
    color: '#e0e0e0',
    border: '3px solid rgba(147, 51, 234, 0.4)',
    borderRadius: '12px',
    padding: '12px',
    fontSize: '18px',
    fontFamily: "'Courier New', monospace",
    fontWeight: 500,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease',
    zIndex: 10,
    '&:hover fieldset': {
      borderColor: '#ec4899',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#ec4899',
      boxShadow: '0 0 8px rgba(236, 72, 153, 0.3)',
    },
    '&.Mui-focused': {
      transform: 'scale(1.02)',
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    fontWeight: 500,
    zIndex: 10,
    padding: '0',
    '&::placeholder': {
      color: '#a0a0a0',
      opacity: 1,
      fontFamily: "'Courier New', monospace",
      fontSize: '18px',
    },
  },
}));

const PlanDetailsBox = styled(motion(Box))(({ theme }) => ({
  background: 'linear-gradient(135deg, #1f2937, #2d3748)',
  borderRadius: '12px',
  padding: theme.spacing(4),
  border: '1px solid rgba(147, 51, 234, 0.3)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), 0 0 15px rgba(147, 51, 234, 0.4)',
  width: '100%',
  maxWidth: '90vw',
  '@media (min-width: 640px)': {
    maxWidth: '500px',
  },
  margin: '0 auto',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.3s ease',
}));

// Plans data
const plans = {
  basic: {
    name: 'Basic',
    price: '$19/mo',
    stripePriceId: 'price_1RBeUK78DLkwVCwCpN1CeuBy',
    features: ['Standard Text Chatbot', 'Up to 5 URLs', 'Document Upload (10MB)', 'Email Support', 'Basic Analytics'],
  },
  pro: {
    name: 'Pro',
    price: '$49/mo',
    stripePriceId: 'price_1NxyzABC1234567891',
    features: ['Advanced Text Chatbot', 'Phone Robot Option', 'Search URLs', 'Document Upload (100MB)', 'Priority Support', 'Advanced Analytics'],
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Contact Us',
    stripePriceId: null,
    features: ['Custom Text & Phone Robots', 'Dedicated Infrastructure', 'Unlimited Uploads', 'Dedicated Account Manager', 'API Access', 'Custom Integrations'],
  },
};

// Helper function to get headers with Firebase token
const getHeaders = async (auth) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (auth.currentUser) {
    try {
      const idToken = await auth.currentUser.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    } catch (tokenError) {
      console.error('Error retrieving ID token:', tokenError);
    }
  }
  return headers;
};

// Payment Form Component
const PaymentForm = ({ plan, setError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { auth } = useFirebase();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [billingName, setBillingName] = useState('');
  const stripeurl = import.meta.env.VITE_STRIPE_ENDPOINT;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !auth.currentUser) {
      setError('Payment system not ready or user not authenticated.');
      return;
    }

    setLoading(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const headers = await getHeaders(auth);
      const response = await fetch(stripeurl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          priceId: plan.stripePriceId,
          amount: parseInt(plan.price.replace('$', '').split('/')[0]) * 100,
          planName: plan.name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = await response.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: billingName || 'Anonymous' },
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent.status === 'succeeded') {
        console.log(`Payment succeeded: ${paymentIntentId}`);
        navigate('/payment/success', { state: { paymentSuccess: true, planName: plan.name } });
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred during payment processing.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ mb: 2, position: 'relative' }}>
        <CustomTextField
          value={billingName}
          onChange={(e) => setBillingName(e.target.value)}
          variant="outlined"
          fullWidth
          placeholder="Cardholder Name"
          InputProps={{
            style: { color: '#e0e0e0', fontWeight: 500, fontFamily: "'Courier New', monospace", fontSize: '18px' },
          }}
        />
      </Box>
      <Box sx={{ mb: 2.5, position: 'relative' }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '18px',
                color: '#e0e0e0',
                fontFamily: "'Courier New', monospace",
                '::placeholder': { color: '#a0a0a0', opacity: 1, fontFamily: "'Courier New', monospace", fontSize: '18px' },
                backgroundColor: 'transparent',
                padding: '12px',
                borderRadius: '12px',
                fontWeight: 500,
                iconColor: '#ffffff',
              },
              invalid: { color: '#ef4444', iconColor: '#ef4444' },
            },
            hidePostalCode: false,
            iconStyle: 'solid',
          }}
        />
      </Box>
      <CustomButton type="submit" disabled={!stripe || loading || !auth.currentUser} fullWidth>
        {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Pay Now'}
      </CustomButton>
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', overflow: 'visible' }}>
        {/* For production, host the logo locally, e.g., /public/assets/powered-by-stripe.png */}
        <img
          src="https://i.imgur.com/AG0xW16.png"
          alt="Powered by Stripe"
          className="w-48 h-15"
          style={{
            filter: 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.3))',
            transition: 'filter 0.3s ease',
          }}
          onMouseEnter={(e) => (e.target.style.filter = 'drop-shadow(0 0 8px rgba(147, 51, 234, 0.5))')}
          onMouseLeave={(e) => (e.target.style.filter = 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.3))')}
          onError={(e) => console.error('Failed to load Stripe logo:', e)}
        />
        <Typography
          variant="caption"
          sx={{
            color: '#e0e0e0',
            textAlign: 'center',
            mt: 1,
            fontSize: '0.75rem',
          }}
        >
          Your payment information is securely handled by Stripe. We do not store or process your card details.
        </Typography>
      </Box>
      <style>{`
        .StripeElement {
          width: 100%;
          background: linear-gradient(145deg, #2d3748, #1f2937);
          border: 3px solid rgba(147, 51, 234, 0.4);
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease;
        }
        .StripeElement--focus {
          border-color: #ec4899;
          box-shadow: 0 0 8px rgba(236, 72, 153, 0.3);
          transform: scale(1.02);
        }
        .StripeElement--invalid {
          border-color: #ef4444;
          animation: shake 0.5s ease;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(147, 51, 234, 0.3); }
          50% { box-shadow: 0 4px 30px rgba(147, 51, 234, 0.6); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .PaymentFormContainer {
          background: linear-gradient(135deg, #1f2937, #9333ea),
                      linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
          background-size: 200% 100%, 100% 100%;
          animation: shimmer 3s infinite linear;
        }
        .contactless {
          position: 'absolute';
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          filter: drop-shadow(0 0 4px rgba(147, 51, 234, 0.3));
          z-index: 30;
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }
        .PaymentFormContainer:hover .contactless {
          opacity: 1;
        }
        .spark {
          position: absolute;
          width: 6px;
          height: 6px;
          background: #ec4899;
          border-radius: 50%;
          animation: spark 0.5s ease-out forwards;
          pointer-events: none;
        }
        @keyframes spark {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2) translate(var(--x), var(--y)); opacity: 0; }
        }
        .particle {
          position: absolute;
          background: rgba(147, 51, 234, 0.5);
          border-radius: 50%;
          animation: float 8s infinite ease-in-out;
        }
        .particle:nth-child(1) { width: 20px; height: 20px; top: 10%; left: 20%; animation-delay: 0s; }
        .particle:nth-child(2) { width: 15px; height: 15px; top: 50%; left: 80%; animation-delay: 1s; }
        .particle:nth-child(3) { width: 25px; height: 25px; top: 80%; left: 30%; animation-delay: 2s; }
        .particle:nth-child(4) { width: 10px; height: 10px; top: 30%; left: 60%; animation-delay: 3s; }
        .particle:nth-child(5) { width: 30px; height: 30px; top: 70%; left: 10%; animation-delay: 4s; }
        @keyframes float {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          50% { transform: translate(20px, -20px); opacity: 0.6; }
        }
        .plan-particle {
          position: absolute;
          background: rgba(147, 51, 234, 0.4);
          border-radius: 50%;
          animation: float 6s infinite ease-in-out;
        }
        .plan-particle:nth-child(1) { width: 15px; height: 15px; top: 20%; left: 10%; animation-delay: 0s; }
        .plan-particle:nth-child(2) { width: 10px; height: 10px; top: 60%; left: 90%; animation-delay: 1s; }
      `}</style>
    </motion.form>
  );
};

// Main PaymentPage Component
const PaymentPage = () => {
  const { selectedPlan } = useWorkflowState();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { auth } = useFirebase();
  const theme = useTheme();
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState(null);

  const planId = state?.planId || selectedPlan;
  const plan = plans[planId] || plans['basic'];

  // Authentication check
  useEffect(() => {
    if (!auth) return;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setAuthReady(true);
      } else {
        navigate('/login', { state: { redirectAfterLogin: '/payment', planId } });
      }
    });
    return () => unsubscribe();
  }, [auth, navigate, planId]);

  // Redirect for invalid plans
  useEffect(() => {
    if (!planId || planId === 'trial') {
      navigate('/wizard');
    } else if (planId === 'enterprise') {
      navigate('/contact');
    }
  }, [planId, navigate]);

  // Handle button click for spark effect
  const handleButtonClick = (event) => {
    const button = event.currentTarget;
    const sparks = 5;
    for (let i = 0; i < sparks; i++) {
      const spark = document.createElement('div');
      spark.className = 'spark';
      const angle = (i / sparks) * 360;
      const radius = 20 + Math.random() * 10;
      spark.style.setProperty('--x', `${Math.cos((angle * Math.PI) / 180) * radius}px`);
      spark.style.setProperty('--y', `${Math.sin((angle * Math.PI) / 180) * radius}px`);
      button.appendChild(spark);
      setTimeout(() => spark.remove(), 500);
    }
  };

  // Note: Update index.html to include as="font" for font preload, e.g.:
  // <link rel="preload" href="/fonts/Inter-VariableFont.woff2" as="font" type="font/woff2" crossorigin>

  // Note: Monitor React Router v7 updates for future flags (v7_startTransition, v7_relativeSplatPath)

  if (!authReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#1f2937' }}>
        <CircularProgress size={40} sx={{ color: '#ec4899' }} />
      </Box>
    );
  }

  return (
    <motion.div
      className="bg-gradient-to-b from-gray-900 to-purple-900 text-gray-300 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      style={{ position: 'relative' }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
              mb: 4,
              textShadow: '0 0 10px rgba(147, 51, 234, 0.5)',
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Activate Your Subscription
          </Typography>
          <PlanDetailsBox
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
            sx={{ mb: { xs: 4, md: 6 } }}
          >
            <div className="plan-particle" />
            <div className="plan-particle" />
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
                mb: 2,
                textShadow: '0 0 10px #9333ea',
              }}
            >
              Selected Plan
            </Typography>
            <Typography sx={{ color: '#e0e0e0', mb: 2, fontWeight: 500 }}>
              <strong>{plan.name}</strong> - {plan.price}
            </Typography>
            <ul style={{ paddingLeft: theme.spacing(2), color: '#e0e0e0' }}>
              {plan.features.map((feature, index) => (
                <motion.li
                  key={index}
                  style={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(1) }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, type: 'spring', stiffness: 200 }}
                >
                  <Check className="w-4 h-4 text-green-400 mr-2" />
                  <span style={{ fontWeight: 500 }}>{feature}</span>
                </motion.li>
              ))}
            </ul>
          </PlanDetailsBox>
          {error && (
            <Alert
              severity="error"
              sx={{
                maxWidth: '90vw',
                '@media (min-width: 640px)': {
                  maxWidth: '500px',
                },
                width: '100%',
                mx: 'auto',
                mt: 2,
                mb: 2,
                bgcolor: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid #ef4444',
                color: '#f8d7da',
                '& .MuiAlert-icon': { color: '#f8d7da' },
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}
            >
              {error}
            </Alert>
          )}
          <PaymentFormContainer
            whileHover={{
              rotateX: 2,
              rotateY: 2,
              boxShadow: '0 12px 40px rgba(147, 51, 234, 0.5)',
            }}
            transition={{ type: 'spring', stiffness: 100 }}
            sx={{ mt: { xs: 2, md: 4 } }}
          >
            <div className="particle" />
            <div className="particle" />
            <div className="particle" />
            <div className="particle" />
            <div className="particle" />
            <svg className="contactless w-8 h-8" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <image href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAQAAAC0NkA6AAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QA/4 MOST RECENT CODE: ePzL8AAAAJcEhZcwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IEzgIwaKTAAADDklEQVRYw+1XS0iUURQ+f5qPyjQflGRFEEFK76koKGxRbWyVVLSOgsCgwjZBJJYuKogSIoOonUK4q3U0WVBWFPZYiIE6kuArG3VGzK/FfPeMM/MLt99/NuHdfPd888/57jn3nvsQWWj/VcMlvMMd5KRTogqx9iCdIjUUmcGR9ImUYowyP3xNGQJoRLVaZ2DaZf8kyjEJALhI28ELioyiwC+Rc3QZwRYyO/DH51hQgWm6DMIh10KmD4u9O16K49itVoPOAmcGAWWOepXIRScAoJZ2Frro8oN+EyTT6lWkkg6msZfMSR35QTJmjU0g15tIGSJ08ZZMJkJkHpNZgSkyXosS13TkJpZ62mPIJvOSzC1bp8vRhhCakEk7G9/o4gmZdbpsTcKu0m63FbnBP9Qrc15zbkbemfgNDtEOI8NO5L5O9VYyRYgmJayZ9nPaxZrSjW4+F6Uw9yQqIiIZwhp2huQTf6OIvCZyGM6gDJBZbyXifJXr7FZjGXsdxADxI7HUJFB6iWvsIhFpkoiIiGTJfjJfiCuJg2ZEspq9EHGVpYgzKqwJqSAOEwuJQ/pxPvE3cYltJCLdxBLiSKKIE5HxJKcTRNeadxfhDiuYw44zVs1dxKwRk/uCxIiQkxKBsSctRVAge9g1E15EHE6yRUaJecRxcWlukdRIbGFOSZCMWQA/iWauIP3slREHXPyliqBcrrD71AmzZ+rD1Mt2Yr8TZc/UR4/YtFnbijnHi3UrN9vKQ9rPaJf867ZiaqDB+czeKYmd3pNa6fuI75MiC0uXXSR5aEMf7s7a6r/PudVXkjFb/SsrCRfROk0Fx6+H1i9kkTGn/E1vEmt1m089fh+RKdQ5O+xNJPUicUIjO0Dm7HwvErEr0YxeibL1StSh37STafE4I7zcBdRq1DiOkdmlTJVnkQTBTS7X1FYyvfO4piaInKbDCDaT2anLudYXCRFsQBgAcIF2/Okwgvz5+Z4tsw118dzruvIvjhTB+HOuWy8UvovEH6beitBKxDyxm9MmISKCWrzB7bSlaqGlsf0FC0gMjzTg6GgAAAAldEVYdGRhdGU6Y3JlYXRlADIwMjMtMDItMTNUMDg6MTk6NTYrMDA6MDCjlq7LAAAAJXRFWHRkYXRlOm1vZGlmyQAyMDIzLTAyLTEzVDA4OjE5OjU2KzAwOjAw0ssWdwAAACh0RVh0ZGF0ZTp0aW1lc3RhbXAAMjAyMy0wMi0xM1QwODoxOTo1NiswMDowMIXeN6gAAAAASUVORK5CYII=" />
            </svg>
            <Elements stripe={stripePromise} options={{ locale: 'en-CA' }}>
              <PaymentForm plan={plan} setError={setError} />
            </Elements>
          </PaymentFormContainer>
        </motion.div>
      </Container>
    </motion.div>
  );
};

export default PaymentPage;